import React from 'react';
import { Link } from 'react-router-dom';
import { PawPrint, BookOpen, Heart, Info, Mail, Calculator, DollarSign, Users, FileText, Sun, Moon, Type, ChevronDown } from 'lucide-react';
import { useAccessibilitySettings } from '../../hooks/useAccessibilitySettings';

interface MobileNavProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  adjustFontSize: (delta: number) => void;
  fontSize: number;
}

export function MobileNav({ 
  isOpen, 
  setIsOpen,
  adjustFontSize,
  fontSize
}: MobileNavProps) {
  const { theme, toggleTheme, isDark, isHighContrast } = useAccessibilitySettings();
  const [isAccessibilityOpen, setIsAccessibilityOpen] = React.useState(false);

  const handleLinkClick = () => {
    setIsOpen(false);
  };

  if (!isOpen) return null;

  return (
    <div
      className="md:hidden fixed inset-x-0 top-14 bottom-0 bg-white dark:bg-accessible-gray-900 high-contrast:bg-black z-[95] shadow-xl" 
      role="navigation" 
      aria-label="Menu mobilne"
      onKeyDown={(e) => {
        if (e.key === 'Escape') {
          setIsOpen(false);
        }
      }}
    >
      {/* Overlay */}
      <div 
        className="absolute inset-0 bg-black/50 z-[90]"
        onClick={() => setIsOpen(false)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            setIsOpen(false);
          }
        }}
      />
      
      {/* Menu Content */}
      <div className="relative z-[100] h-full bg-white dark:bg-accessible-gray-900 high-contrast:bg-black overflow-y-auto">
        <div className="pt-10 pb-4 space-y-2 px-4">
          {/* Accessibility Controls */}
          <div className="py-2 text-base font-medium text-accessible-gray-800 dark:text-white high-contrast:text-white">
            <button
              onClick={() => setIsAccessibilityOpen(!isAccessibilityOpen)}
              className="flex items-center justify-between w-full py-2"
            >
              <div className="flex items-center gap-2">
              <Type className="w-4 h-4" />
              <span>Dostępność</span>
              </div>
              <ChevronDown className={`w-4 h-4 transition-transform ${isAccessibilityOpen ? 'rotate-180' : ''}`} />
            </button>
            <div className={`pl-6 space-y-3 overflow-hidden transition-all duration-300 ${isAccessibilityOpen ? 'max-h-[300px] mt-2' : 'max-h-0'}`}>
              {/* Theme Controls */}
              <div className="space-y-2">
                <button
                  onClick={() => {
                    toggleTheme('light');
                  }}
                  className={`flex items-center gap-2 w-full py-2 px-3 rounded-lg transition-colors ${
                    theme === 'light'
                      ? 'bg-accessible-red-600 text-white'
                      : 'text-accessible-gray-700 dark:text-white high-contrast:text-white hover:bg-accessible-gray-100 dark:hover:bg-accessible-gray-700 high-contrast:hover:bg-white/10'
                  }`}
                >
                  <Sun className="w-4 h-4" />
                  <span>Tryb jasny</span>
                </button>
                <button
                  onClick={() => {
                    toggleTheme('dark');
                  }}
                  className={`flex items-center gap-2 w-full py-2 px-3 rounded-lg transition-colors ${
                    theme === 'dark'
                      ? 'bg-accessible-red-600 text-white'
                      : 'text-accessible-gray-700 dark:text-white high-contrast:text-white hover:bg-accessible-gray-100 dark:hover:bg-accessible-gray-700 high-contrast:hover:bg-white/10'
                  }`}
                >
                  <Moon className="w-4 h-4" />
                  <span>Tryb ciemny</span>
                </button>
                <button
                  onClick={() => {
                    toggleTheme('high-contrast');
                  }}
                  className={`flex items-center gap-2 w-full py-2 px-3 rounded-lg transition-colors ${
                    theme === 'high-contrast'
                      ? 'bg-accessible-red-600 text-white'
                      : 'text-accessible-gray-700 dark:text-white high-contrast:text-white hover:bg-accessible-gray-100 dark:hover:bg-accessible-gray-700 high-contrast:hover:bg-white/10'
                  }`}
                >
                  <Type className="w-4 h-4" />
                  <span>Wysoki kontrast</span>
                </button>
              </div>
              
              {/* Font Size Controls */}
              <div className="flex items-center justify-between gap-2 py-2">
                <span className="text-accessible-gray-700 dark:text-white high-contrast:text-white">Rozmiar tekstu</span>
                <div className="flex items-center gap-1 flex-wrap">
                  <button
                    onClick={() => adjustFontSize(90 - fontSize)}
                    className={`px-2 py-1 rounded-lg text-sm hover:bg-accessible-gray-100 dark:hover:bg-accessible-gray-700 high-contrast:hover:bg-white/10 ${Math.abs(fontSize - 90) < 6 ? 'bg-accessible-red-600 text-white' : 'text-accessible-gray-700 dark:text-white high-contrast:text-white'}`}
                    aria-label="Ustaw rozmiar tekstu: mały"
                    title="Ustaw rozmiar tekstu: mały"
                  >
                    A-
                  </button>
                  <button
                    onClick={() => adjustFontSize(100 - fontSize)}
                    className={`px-2 py-1 rounded-lg text-sm hover:bg-accessible-gray-100 dark:hover:bg-accessible-gray-700 high-contrast:hover:bg-white/10 ${Math.abs(fontSize - 100) < 6 ? 'bg-accessible-red-600 text-white' : 'text-accessible-gray-700 dark:text-white high-contrast:text-white'}`}
                    aria-label="Ustaw rozmiar tekstu: standardowy"
                    title="Ustaw rozmiar tekstu: standardowy"
                  >
                    A
                  </button>
                  <button
                    onClick={() => adjustFontSize(115 - fontSize)}
                    className={`px-2 py-1 rounded-lg text-sm hover:bg-accessible-gray-100 dark:hover:bg-accessible-gray-700 high-contrast:hover:bg-white/10 ${Math.abs(fontSize - 115) < 6 ? 'bg-accessible-red-600 text-white' : 'text-accessible-gray-700 dark:text-white high-contrast:text-white'}`}
                    aria-label="Ustaw rozmiar tekstu: duży"
                    title="Ustaw rozmiar tekstu: duży"
                  >
                    A+
                  </button>
                  <button
                    onClick={() => adjustFontSize(130 - fontSize)}
                    className={`px-2 py-1 rounded-lg text-sm hover:bg-accessible-gray-100 dark:hover:bg-accessible-gray-700 high-contrast:hover:bg-white/10 ${Math.abs(fontSize - 130) < 6 ? 'bg-accessible-red-600 text-white' : 'text-accessible-gray-700 dark:text-white high-contrast:text-white'}`}
                    aria-label="Ustaw rozmiar tekstu: bardzo duży"
                    title="Ustaw rozmiar tekstu: bardzo duży"
                  >
                    A++
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Adopcja */}
          <div className="py-2 text-base font-medium text-accessible-gray-800 dark:text-white high-contrast:text-white">
            <div className="flex items-center gap-2 mb-2">
              <PawPrint className="w-4 h-4" />
              <span>Adopcja</span>
            </div>
            <div className="pl-6 space-y-2">
              <Link
            to="/zwierzeta"
            onClick={handleLinkClick}
            className="block py-2 text-accessible-gray-700 dark:text-white high-contrast:text-white hover:text-accessible-gray-900 dark:hover:text-accessible-red-600 high-contrast:hover:text-accessible-red-600"
          >
            Zwierzęta
              </Link>
              <Link
                to="/adoptuj"
                onClick={handleLinkClick}
                className="block py-2 text-accessible-gray-700 dark:text-white high-contrast:text-white hover:text-accessible-gray-900 dark:hover:text-accessible-red-600 high-contrast:hover:text-accessible-red-600"
              >
                Proces adopcji
              </Link>
              <Link
                to="/wirtualna-adopcja"
                onClick={handleLinkClick}
                className="block py-2 text-accessible-gray-700 dark:text-white high-contrast:text-white hover:text-accessible-gray-900 dark:hover:text-accessible-red-600 high-contrast:hover:text-accessible-red-600"
              >
                Adopcja wirtualna
              </Link>
              <Link
                to="/dom-tymczasowy"
                onClick={handleLinkClick}
                className="block py-2 text-accessible-gray-700 dark:text-white high-contrast:text-white hover:text-accessible-gray-900 dark:hover:text-accessible-red-600 high-contrast:hover:text-accessible-red-600"
              >
                Dom tymczasowy
              </Link>
            </div>
          </div>

          {/* Wsparcie */}
          <div className="py-2 text-base font-medium text-accessible-gray-800 dark:text-white high-contrast:text-white">
            <div className="flex items-center gap-2 mb-2">
              <DollarSign className="w-4 h-4" />
              <span>Wsparcie</span>
            </div>
            <div className="pl-6 space-y-2">
              <Link
                to="/wsparcie"
                onClick={handleLinkClick}
                className="block py-2 text-accessible-gray-700 dark:text-white high-contrast:text-white hover:text-accessible-gray-900 dark:hover:text-accessible-red-600 high-contrast:hover:text-accessible-red-600"
              >
                Jak pomóc
              </Link>
              <Link
                to="/wplata-jednorazowa"
                onClick={handleLinkClick}
                className="block py-2 text-accessible-red-600 font-semibold hover:text-accessible-red-700"
              >
                WPŁAĆ TERAZ
              </Link>
              <Link
                to="/przekaz-podatek"
                onClick={handleLinkClick}
                className="block py-2 text-accessible-gray-700 dark:text-white high-contrast:text-white hover:text-accessible-gray-900 dark:hover:text-accessible-red-600 high-contrast:hover:text-accessible-red-600"
              >
                Przekaż 1.5% podatku
              </Link>
              <Link
                to="/wolontariat"
                onClick={handleLinkClick}
                className="block py-2 text-accessible-gray-700 dark:text-white high-contrast:text-white hover:text-accessible-gray-900 dark:hover:text-accessible-red-600 high-contrast:hover:text-accessible-red-600"
              >
                Zostań wolontariuszem
              </Link>
            </div>
          </div>

          {/* Aktualności */}
          <div className="py-2 text-base font-medium text-accessible-gray-800 dark:text-white high-contrast:text-white">
            <div className="flex items-center gap-2 mb-2">
              <BookOpen className="w-4 h-4" />
              <span>Aktualności</span>
            </div>
            <div className="pl-6 space-y-2">
              <Link
                to="/aktualnosci"
                onClick={handleLinkClick}
                className="block py-2 text-accessible-gray-700 dark:text-white high-contrast:text-white hover:text-accessible-gray-900 dark:hover:text-accessible-red-600 high-contrast:hover:text-accessible-red-600"
              >
                Blog
              </Link>
              <Link
                to="/historie-adopcyjne"
                onClick={handleLinkClick}
                className="block py-2 text-accessible-gray-700 dark:text-white high-contrast:text-white hover:text-accessible-gray-900 dark:hover:text-accessible-red-600 high-contrast:hover:text-accessible-red-600 no-margin"
              >
                Historie adopcyjne
              </Link>
              <Link
                to="/za-teczowym-mostem"
                onClick={handleLinkClick}
                className="block py-2 text-accessible-gray-700 dark:text-white high-contrast:text-white hover:text-accessible-gray-900 dark:hover:text-accessible-red-600 high-contrast:hover:text-accessible-red-600"
              >
                Za tęczowym mostem
              </Link>
              <Link
                to="/sprawozdania"
                onClick={handleLinkClick}
                className="block py-2 text-accessible-gray-700 dark:text-white high-contrast:text-white hover:text-accessible-gray-900 dark:hover:text-accessible-red-600 high-contrast:hover:text-accessible-red-600"
              >
                Sprawozdania
              </Link>
            </div>
          </div>

          {/* O nas */}
          <Link
            to="/o-nas"
            onClick={handleLinkClick}
            className="block px-4 py-2 text-base font-medium text-accessible-gray-800 dark:text-white high-contrast:text-white hover:bg-accessible-gray-200 dark:hover:bg-accessible-gray-700 rounded-lg flex items-center gap-2"
          >
            <Info className="w-4 h-4" />
            O nas
          </Link>

          {/* Kontakt */}
          <Link
            to="/kontakt"
            onClick={handleLinkClick}
            className="block px-4 py-2 text-base font-medium text-accessible-gray-800 dark:text-white high-contrast:text-white hover:bg-accessible-gray-200 dark:hover:bg-accessible-gray-700 rounded-lg flex items-center gap-2"
          >
            <Mail className="w-4 h-4" />
            Kontakt
          </Link>
          
          {/* Language removed temporarily */}
        </div>
      </div>
    </div>
  );
}